import React from "react";
import { FlatButton, TextField, RaisedButton } from "material-ui";
import TimePicker from "material-ui/TimePicker";
import Select from "react-select";
import { DateRange } from "react-date-range";
import moment from "moment-timezone";
import { RRule, rrulestr, RRuleSet } from "rrule";
import BaseOverlay from "./base.jsx";
import Label from "../label.jsx";
import update from "immutability-helper";
import IconButton from "material-ui/IconButton";
import FloatingActionButton from "material-ui/FloatingActionButton";
// import ActionAdd from 'material-ui/svg-icons/action/alarm-add';
import ContentAdd from "material-ui/svg-icons/content/add";
import ContentRemove from "material-ui/svg-icons/content/remove";
import DeleteIcon from "material-ui/svg-icons/action/delete";
import momentTimezone from "moment-timezone";
import RecurringUpdateOptions from "../events/recurring-update-options.jsx";

import DateTimeField from "../datetime-field.jsx";
import { timezoneDelta } from "../datetime-picker.jsx";

// Todo: unify
const OPTIONS = {
  CURRENT: "single",
  FUTURE: "future",
  ALL: "all"
};

const styles = {
  timePicker: { display: "inline-block", minWidth: 80 },
  timePickerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative"
  },
  addIcon: {
    position: "absolute",
    right: 0
  },
  removeIcon: {
    position: "absolute",
    right: 0
  }
};

const WEEKDAYS = [
  { label: "F", value: 4 },
  { label: "S", value: 5 },
  { label: "Su", value: 6 },
  { label: "M", value: 0 },
  { label: "Tu", value: 1 },
  { label: "W", value: 2 },
  { label: "Th", value: 3 }
];

/*
 * The below two functions are needed because material-ui's TimePicker only works with dates in the local timezone.
 */

// Converts a date with a timezone to one without, i.e., Jun 10, 2019 5:00 PM PST --> June 10, 2019 5:00 PM UTC
export const stripTimeZone = d =>
  new Date(
    Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      d.getHours(),
      d.getMinutes(),
      d.getSeconds()
    )
  );

// Converts a date without a timezone back to a date with the current time zone -- opposite of stripTimeZone. i.e., Jun 10, 2019 5:00 PM UTC --> June 10, 2019 5:00 PM PST
export const appendTimeZone = d =>
  new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds()
  );

// Sets the month and date and year of *d* to that of *date*.
const setMonthDateYear = (d, date) => {
  if (date._isAMomentObject) {
    date = date.toDate();
  }
  if (d._isAMomentObject) {
    d = d.toDate();
  }
  let d_ = new Date(d.getTime());
  d_.setDate(date.getDate());
  d_.setMonth(date.getMonth());
  d_.setYear(date.getFullYear());
  return d_;
};

// Gets the difference between *endTime* and *startTime*.
// TODO: If endTime < startTime, should we add one day to endTime (for example when an event is happening from 11 PM - 1 AM)?
const getDifference = (endTime, startTime) => {
  return endTime.getTime() - startTime.getTime();
};

// Convert timezone to moment.js format normalized identifier. To do this, we filter down the set
// of timezones to only the "valid" ones for each country, then if a link exists between zones to
// a valid one, we use that instead.
// For example, converting US/Central -> America/Chicago
// Ref: https://github.com/moment/moment-timezone/issues/227#issuecomment-629098169
const _generateValidTimezones = () => {
  const zones = new Set();
  const countries = moment.tz.countries();
  for (const country of countries) {
    moment.tz
      .zonesForCountry(country)
      .reduce((set, zone) => set.add(zone), zones);
  }
  return Array.from(zones).sort();
};
const _tzToIdentifier = name => (name || "").toLowerCase().replace(/\//g, "_");
const _normalizeTimezone = name => {
  const link = moment.tz._links[_tzToIdentifier(name)];
  if (link) {
    const baseTz = moment.tz._zones[link];
    if (baseTz && _generateValidTimezones().indexOf(baseTz.name) !== -1) {
      return baseTz.name;
    }
  }
  return name;
};

class SchedulerOverlay extends React.Component {
  constructor(props) {
    super(props);

    // Rewrite timezone to an IANA standard (non-deprecated) timezone. This is because rrule is
    // picky about matching exactly, and has a smaller subset of available options.
    let timezone = _normalizeTimezone(this.props.timezone);
    console.log(
      `[SchedulerOverlay] Loaded timezone: ${this.props.timezone} => ${timezone}`
    );

    if (this.props.repeats) {
      const startDate = moment(
        appendTimeZone(rrulestr(this.props.repeats[0].rrule).options.dtstart)
      );
      const endDate = moment(
        appendTimeZone(rrulestr(this.props.repeats[0].rrule).options.until)
      );
      startDate.add(timezoneDelta(startDate, timezone), "minutes");
      endDate.add(timezoneDelta(endDate, timezone), "minutes");

      this.state = {
        timezone,
        applyRecurring: OPTIONS.ALL,
        showApplyRecurring: true,
        startDate,
        endDate,
        repeatsInvalid: new Set(),
        repeats: this.props.repeats.map(({ rrule, duration }) => {
          rrule = rrulestr(rrule);
          const startDate = moment(appendTimeZone(rrule.options.dtstart));
          const endDate = moment(appendTimeZone(rrule.options.until));
          startDate.add(timezoneDelta(startDate, timezone), "minutes");
          endDate.add(timezoneDelta(endDate, timezone), "minutes");

          return {
            startTime: startDate.toDate(),
            endTime: moment(startDate).add(duration, "ms").toDate(),
            freq: RRule.WEEKLY, //rrule.options.freq,
            interval: 1, //rrule.options.interval,
            byweekday: new Set(rrule.options.byweekday || []),
            tzid: timezone
          };
        })
      };
    } else {
      this.state = {
        timezone,
        applyRecurring: OPTIONS.CURRENT,
        showApplyRecurring: false,
        startDate: this.props.start_time
          ? this.props.start_time.toDate()
          : moment().toDate(),
        endDate: this.props.start_time
          ? moment(this.props.start_time.toDate()).add(7, "days").toDate()
          : moment().add(7, "days").toDate(),
        repeatsInvalid: new Set(),
        repeats: [
          {
            startTime: this.props.start_time
              ? this.props.start_time.toDate()
              : null,
            endTime: this.props.end_time ? this.props.end_time.toDate() : null,
            freq: RRule.WEEKLY,
            interval: 1,
            byweekday: new Set()
          }
        ]
      };
    }

    this.validateRepeat = this.validateRepeat.bind(this)
    this.validateAllRepeats = this.validateAllRepeats.bind(this)
  }

  validateRepeat(repeat, options = {}) {
    const startTimeMoment = moment(this.state.startDate)
    const endTimeMoment = moment(this.state.endDate)

    const daysDifference = endTimeMoment.diff(startTimeMoment, 'days') + 1

    if (daysDifference >= 7) {
      this.setState((state) => {
        const newInvalidRepeats = new Set(state.repeatsInvalid)
        newInvalidRepeats.delete(options.repeatIndex)

        return { repeatsInvalid: newInvalidRepeats}
      })
      return true;
    }

    let currentDay = startTimeMoment.day()
    const endDay = endTimeMoment.day()
    const availableDays = [endDay]

    while (currentDay !== endDay) {
      availableDays.push(currentDay)

      if (currentDay === 6) {
        currentDay = 0
      } else {
        currentDay++
      }
    }

    //convert available days to an array of available days where Monday is the zero-th day.
    //this will make it valid with WEEKDAYS "enum"
    const convertedAvailableDays = availableDays.map((day) => {
      if (day === 0) {
        return 6
      }

      return day - 1
    })

    for (const weekDay of repeat.byweekday) {
      if (weekDay === options.recentlyRemoved) {
        continue
      }

      if (!convertedAvailableDays.includes(weekDay)) {
        this.setState((state) => ({ repeatsInvalid: new Set(state.repeatsInvalid).add(options.repeatIndex) }));
        return false
      }
    }

    if (options.recentlyAdded && !convertedAvailableDays.includes(options.recentlyAdded)) {
      this.setState((state) => ({ repeatsInvalid: new Set(state.repeatsInvalid).add(options.repeatIndex) }));
      return false
    }

    this.setState((state) => {
      const newInvalidRepeats = new Set(state.repeatsInvalid)
      newInvalidRepeats.delete(options.repeatIndex)

      return { repeatsInvalid: newInvalidRepeats}
    })
    return true;
  }

  validateAllRepeats(recentlyRemoved = -1) {
    return this.state.repeats.reduce((count, repeat, index) => {
      if (index === recentlyRemoved) {
        this.setState((state) => {
          const newInvalidRepeats = new Set(state.repeatsInvalid)
          newInvalidRepeats.delete(recentlyRemoved)

          return { repeatsInvalid: newInvalidRepeats}
        })
        return count
      }

      return count + (this.validateRepeat(repeat, { repeatIndex: index }) ? 0 : 1)
    }, 0)
  }

  save() {
    const invalidRepeats = this.validateAllRepeats()

    if (invalidRepeats > 0) {
      return
    }
    const repeats = this.state.repeats.map(
      ({
        freq,
        interval,
        startDate = this.state.startDate,
        endDate = this.state.endDate,
        startTime,
        endTime,
        byweekday,
        tzid
      }) => {
        const start = stripTimeZone(setMonthDateYear(startTime, startDate));
        const end = stripTimeZone(setMonthDateYear(endTime, endDate));
        const firstEventEnd = stripTimeZone(
          setMonthDateYear(endTime, startDate)
        );

        let delta = timezoneDelta(start, this.state.timezone);
        let convertedStart = moment(start).add(-delta, "minutes").toDate();
        let convertedEnd = moment(end).add(-delta, "minutes").toDate();

        return {
          rrule: new RRule({
            freq,
            interval,
            dtstart: convertedStart,
            until: convertedEnd,
            byweekday: Array.from(byweekday),
            tzid: this.state.timezone // force update
          }).toString(),
          duration: getDifference(firstEventEnd, start)
        };
      }
    );
    this.props.onChange(
      { repeats, applyRecurring: this.state.applyRecurring },
      this.props.onRequestClose
    );
  }

  setRepeatItem(i, key, value) {
    return this.setState({
      repeats: update(this.state.repeats, { [i]: { [key]: { $set: value } } })
    });
  }

  addByWeekday(i, value) {
    this.validateRepeat(this.state.repeats[i], { recentlyAdded: value, repeatIndex: i })
    return this.setState({
      repeats: update(this.state.repeats, {
        [i]: { byweekday: { $add: [value] } }
      })
    });
  }

  removeByWeekday(i, value) {
    this.validateRepeat(this.state.repeats[i], { recentlyRemoved: value, repeatIndex: i })
    return this.setState({
      repeats: update(this.state.repeats, {
        [i]: { byweekday: { $remove: [value] } }
      })
    });
  }

  addRepeat() {
    let newRepeat = { ...this.state.repeats[0] };
    newRepeat.startTime = null;
    newRepeat.endTime = null;
    newRepeat.byweekday = new Set();
    return this.setState({
      repeats: update(this.state.repeats, { $push: [newRepeat] })
    });
  }

  removeRepeat(i) {
    this.validateAllRepeats(i)

    return this.setState({
      repeats: update(this.state.repeats, { $splice: [[i, 1]] })
    });
  }

  setAllDates({ startDate, endDate }) {
    this.setState({
      startDate,
      endDate
    });
  }

  render() {
    let { name } = this.props;
    return (
      <BaseOverlay
        onRequestClose={e => this.props.onRequestClose()}
        useMuiDefaultStyle={true}
        actions={[
          <FlatButton
            label="Cancel"
            onTouchTap={e => this.props.onRequestClose()}
          />,
          <FlatButton
            primary={true}
            label="Save showtimes"
            disabled={this.state.repeatsInvalid.size !== 0}
            onTouchTap={e => this.save()}
          />
        ]}
      >
        <div style={{ marginBottom: 10 }}>Edit the schedule for "{name}".</div>
        <div>
          <DateTimeField
            value={this.state.startDate}
            label="First day of run"
            unclearable={true}
            hideTime={true}
            onChange={(e, v) => this.setState({ startDate: v })}
            style={{ display: "inline-block", marginRight: 24 }}
          />
          <DateTimeField
            value={this.state.endDate}
            label="Last day of run"
            unclearable={true}
            hideTime={true}
            onChange={(e, v) => this.setState({ endDate: v })}
            style={{ display: "inline-block" }}
          />
        </div>
        <Label style={{ marginBottom: -2 }}>Showtimes</Label>
        {this.state.repeats.map((repeat, i) => (
          <div key={i} style={{}}>
            {false && (
              <div>
                <Label>Schedule</Label>
                <div style={{ display: "flex" }}>
                  <div style={{ flex: 1, width: 120 }}>
                    Every
                    <TextField
                      hintText={``}
                      value={repeat.interval}
                      style={{
                        display: "inline-block",
                        marginLeft: 20,
                        maxWidth: 50,
                        textAlign: "right"
                      }}
                      onChange={e =>
                        this.setRepeatItem(i, "interval", e.target.value)
                      }
                    />
                  </div>
                  <div style={{ flex: 2, alignSelf: "center", maxWidth: 300 }}>
                    <Select
                      value={repeat.freq}
                      options={[
                        { label: "day", value: RRule.DAILY },
                        { label: "week", value: RRule.WEEKLY },
                        { label: "month", value: RRule.MONTHLY },
                        { label: "year", value: RRule.YEARLY }
                      ].map(e => ({
                        ...e,
                        label: e.label + (repeat.freq > 1 ? "s" : "")
                      }))}
                      onChange={option =>
                        option && this.setRepeatItem(i, "freq", option.value)
                      }
                    />
                  </div>
                </div>
              </div>
            )}
            <div style={styles.timePickerRow}>
              <DateTimeField
                value={repeat.startTime}
                hintText="Start time"
                unclearable={true}
                hideDate={true}
                timezone={this.state.timezone}
                useMoment={true}
                onChange={(e, d) => this.setRepeatItem(i, "startTime", d)}
                style={styles.timePicker}
              />
              <div style={{ padding: "0 20px" }}>to</div>
              <DateTimeField
                value={repeat.endTime}
                hintText="End time"
                unclearable={true}
                hideDate={true}
                timezone={this.state.timezone}
                useMoment={true}
                onChange={(e, d) => this.setRepeatItem(i, "endTime", d)}
                style={styles.timePicker}
              />
              <div style={{ paddingLeft: 50, flex: 10 }}>
                {WEEKDAYS.map(({ label, value }) => (
                  <RaisedButton
                    label={label}
                    primary={repeat.byweekday.has(value)}
                    style={{ marginRight: 5 }}
                    labelStyle={{ textTransform: "initial" }}
                    onClick={() =>
                      repeat.byweekday.has(value)
                        ? this.removeByWeekday(i, value)
                        : this.addByWeekday(i, value)
                    }
                  />
                ))}
              </div>
              {i === this.state.repeats.length - 1 ? (
                <IconButton
                  style={styles.addIcon}
                  onClick={e => this.addRepeat()}
                  tooltip="Add recurrence"
                  disabled={!repeat.startTime || !repeat.endTime}
                >
                  <ContentAdd />
                </IconButton>
              ) : (
                <IconButton
                  style={styles.removeIcon}
                  onClick={e => this.removeRepeat(i)}
                  tooltip="Remove recurrence"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </div>
          </div>
        ))}
        {this.state.showApplyRecurring && (
          <div>
            <Label>Apply to:</Label>
            <RecurringUpdateOptions
              value={this.state.applyRecurring}
              onChange={e => this.setState({ applyRecurring: e })}
              hideCurrent={true}
            />
          </div>
        )}
        {this.state.repeatsInvalid.size !== 0 && (
          <p>
            The scheduled showtime is not available in between the first and last day of the run, please check your showtimes to ensure they occur during the scheduled run of your event.
          </p>
        )}
      </BaseOverlay>
    );
  }
}

export default SchedulerOverlay;



// WEBPACK FOOTER //
// ./src/app/components/partials/overlays/scheduler-overlay.jsx